import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FieldMapping } from '../../services/organizationService';

interface Organization {
  id: string;
  name: string;
  description?: string;
  asanaProject?: string;
}

export interface StructuredCaptureData {
  requestType: 'op' | 'urgent';
  targetYear: string;
  selectedOrgId: string;
  annualizedEntitlement: number;
  impactMetric: string;
  /** Dynamic prompt field values keyed by fieldName */
  promptValues?: Record<string, any>;
}

interface StructuredCaptureCardProps {
  finalJSON: any;
  organizations: Organization[];
  selectedOrgId: string;
  fieldMappings?: FieldMapping[];
  onContinue: (data: StructuredCaptureData) => void;
  onBackToChat: () => void;
  disabled?: boolean;
}

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;

const ENTITLEMENT_PRESETS = [
  { label: '$100K', value: 100000 },
  { label: '$500K', value: 500000 },
  { label: '$1M', value: 1000000 },
  { label: '$5M', value: 5000000 },
  { label: '$10M+', value: 10000000 },
  { label: 'TBD', value: 0 },
];

/**
 * StructuredCaptureCard — Config-driven sequential inline questions.
 * 
 * Step 0: Portfolio selection (if multiple)
 * Steps 1..N: Dynamic prompt fields from fieldMappings (source === 'prompt')
 * 
 * Falls back to hardcoded steps if no fieldMappings with promptConfig are provided.
 */
export const StructuredCaptureCard: React.FC<StructuredCaptureCardProps> = ({
  finalJSON,
  organizations,
  selectedOrgId: initialOrgId,
  fieldMappings,
  onContinue,
  onBackToChat,
  disabled = false,
}) => {
  // Build portfolio options
  const validOrgs: Array<{id: string; name: string; asanaProject?: string}> = [];
  organizations.forEach(org => {
    const portfolios = (org as any).portfolios as any[] | undefined;
    if (portfolios && portfolios.length > 0) {
      portfolios.forEach((p: any) => {
        validOrgs.push({
          id: p.asanaProjectId,
          name: organizations.length > 1 || portfolios.length > 1 ? `${org.name} → ${p.name}` : p.name,
          asanaProject: p.asanaProjectId,
        });
      });
    } else if (org.asanaProject) {
      validOrgs.push({ id: org.asanaProject, name: org.name, asanaProject: org.asanaProject });
    }
  });

  // Get prompt fields from config, sorted by order
  const promptFields = (fieldMappings || [])
    .filter(f => f.source === 'prompt' && f.promptConfig)
    .sort((a, b) => (a.promptConfig!.order || 0) - (b.promptConfig!.order || 0));

  const hasConfiguredPrompts = promptFields.length > 0;
  const totalPromptSteps = hasConfiguredPrompts ? promptFields.length : 3; // fallback: 3 hardcoded steps

  const hasMultipleOrgs = validOrgs.length > 1;
  const [step, setStep] = useState(hasMultipleOrgs ? 0 : 1);
  const [selectedOrg, setSelectedOrg] = useState(initialOrgId || (validOrgs.length === 1 ? validOrgs[0]?.id : ''));
  
  // Dynamic prompt values
  const [promptValues, setPromptValues] = useState<Record<string, any>>({});
  const [currencyInput, setCurrencyInput] = useState('');

  // Legacy state for backward compat
  const [requestType, setRequestType] = useState<'op' | 'urgent' | null>(null);

  const handleOrgSelect = useCallback((orgId: string) => {
    setSelectedOrg(orgId);
    setStep(1);
  }, []);

  const handlePromptAnswer = useCallback((fieldName: string, value: any) => {
    setPromptValues(prev => ({ ...prev, [fieldName]: value }));
    
    // Find current prompt index
    const currentPromptIndex = step - 1; // step 1 = first prompt (index 0)
    const isLastPrompt = currentPromptIndex >= totalPromptSteps - 1;
    
    if (isLastPrompt) {
      // All prompts answered — submit
      const allValues = { ...promptValues, [fieldName]: value };
      
      setTimeout(() => {
        onContinue({
          requestType: (allValues.request_type === 'OP Request' || allValues.request_type === 'op') ? 'op' : 'urgent',
          targetYear: allValues.request_type === 'OP Request' || allValues.request_type === 'op' ? String(nextYear) : String(currentYear),
          selectedOrgId: selectedOrg,
          annualizedEntitlement: parseFloat(String(allValues.annualized_entitlement || '0').replace(/[^0-9.]/g, '')) || 0,
          impactMetric: allValues.impact_metrics || '',
          promptValues: allValues,
        });
      }, 300);
    } else {
      setStep(step + 1);
    }
  }, [step, totalPromptSteps, promptValues, selectedOrg, onContinue]);

  // Auto-scroll
  const flowEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      flowEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
    return () => clearTimeout(timer);
  }, [step]);

  // Render a single prompt step based on its config
  const renderPromptStep = (field: FieldMapping, promptIndex: number) => {
    const stepNumber = promptIndex + 1; // step 1 = first prompt
    const isCurrentStep = step === stepNumber;
    const isAnswered = step > stepNumber;
    const config = field.promptConfig!;
    const value = promptValues[field.fieldName];

    return (
      <div key={field.fieldName} className="sc-step">
        <div className="sc-question">
          <div className="sc-avatar">🐴</div>
          <div className="sc-bubble">
            <p>{config.question}</p>
            
            {isCurrentStep && config.inputType === 'chips' && (
              <div className="sc-chips">
                {(config.options || []).map(option => (
                  <button
                    key={option}
                    type="button"
                    className={`sc-chip${value === option ? ' selected' : ''}`}
                    onClick={() => handlePromptAnswer(field.fieldName, option)}
                    disabled={disabled}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

            {isCurrentStep && config.inputType === 'dropdown' && (
              <select
                className="sc-dropdown"
                value={value || ''}
                onChange={(e) => e.target.value && handlePromptAnswer(field.fieldName, e.target.value)}
                disabled={disabled}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', width: '100%', marginTop: '8px' }}
              >
                <option value="">Select...</option>
                {(config.options || []).map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            )}

            {isCurrentStep && config.inputType === 'currency' && (
              <>
                <p className="sc-helper">Annual savings or revenue impact in USD</p>
                <div className="sc-chips">
                  {ENTITLEMENT_PRESETS.map(p => (
                    <button
                      key={p.label}
                      type="button"
                      className="sc-chip sc-chip-small"
                      onClick={() => {
                        setCurrencyInput(p.value > 0 ? p.value.toLocaleString('en-US') : 'TBD');
                        handlePromptAnswer(field.fieldName, p.value);
                      }}
                      disabled={disabled}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="sc-currency-row">
                  <span className="sc-currency-prefix">$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="sc-currency-input"
                    placeholder="Or type a custom amount..."
                    value={currencyInput}
                    onChange={e => setCurrencyInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && currencyInput) {
                        const parsed = parseFloat(currencyInput.replace(/[^0-9.]/g, ''));
                        handlePromptAnswer(field.fieldName, isNaN(parsed) ? 0 : parsed);
                      }
                    }}
                    disabled={disabled}
                  />
                  {currencyInput && (
                    <button
                      type="button"
                      className="sc-currency-go"
                      onClick={() => {
                        const parsed = parseFloat(currencyInput.replace(/[^0-9.]/g, ''));
                        handlePromptAnswer(field.fieldName, isNaN(parsed) ? 0 : parsed);
                      }}
                      disabled={disabled}
                    >
                      →
                    </button>
                  )}
                </div>
              </>
            )}

            {isCurrentStep && config.inputType === 'text' && (
              <div style={{ marginTop: '8px' }}>
                <input
                  type="text"
                  className="sc-currency-input"
                  placeholder={config.options?.[0] || 'Type your answer...'}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && (e.target as HTMLInputElement).value) {
                      handlePromptAnswer(field.fieldName, (e.target as HTMLInputElement).value);
                    }
                  }}
                  disabled={disabled}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px' }}
                />
              </div>
            )}

            {isCurrentStep && config.inputType === 'number' && (
              <div style={{ marginTop: '8px' }}>
                <input
                  type="number"
                  className="sc-currency-input"
                  placeholder="Enter a number..."
                  onKeyDown={e => {
                    if (e.key === 'Enter' && (e.target as HTMLInputElement).value) {
                      handlePromptAnswer(field.fieldName, parseFloat((e.target as HTMLInputElement).value));
                    }
                  }}
                  disabled={disabled}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px' }}
                />
              </div>
            )}

            {isCurrentStep && config.inputType === 'date' && (
              <div style={{ marginTop: '8px' }}>
                <input
                  type="date"
                  onChange={e => e.target.value && handlePromptAnswer(field.fieldName, e.target.value)}
                  disabled={disabled}
                  style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px' }}
                />
              </div>
            )}
          </div>
        </div>

        {isAnswered && value !== undefined && (
          <div className="sc-answer">
            <div className="sc-answer-chip">
              {config.inputType === 'currency' 
                ? `💰 ${typeof value === 'number' && value > 0 ? `$${value.toLocaleString('en-US')}/year` : 'TBD'}`
                : `📊 ${value}`
              }
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sc-flow">
      {/* Step 0: Portfolio Selection */}
      {hasMultipleOrgs && (
        <div className="sc-step">
          <div className="sc-question">
            <div className="sc-avatar">🐴</div>
            <div className="sc-bubble">
              <p>Which team should this request go to?</p>
              <div className="sc-chips">
                {validOrgs.map(org => (
                  <button
                    key={org.id}
                    type="button"
                    className={`sc-chip${selectedOrg === org.id ? ' selected' : ''}`}
                    onClick={() => handleOrgSelect(org.id)}
                    disabled={disabled || step > 0}
                  >
                    {org.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {selectedOrg && step >= 1 && (
            <div className="sc-answer">
              <div className="sc-answer-chip">
                🏢 {validOrgs.find(o => o.id === selectedOrg)?.name || selectedOrg}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dynamic Prompt Steps */}
      {hasConfiguredPrompts ? (
        // Render from field mappings config
        promptFields.map((field, idx) => {
          if (step < idx + 1) return null; // Don't render future steps
          return renderPromptStep(field, idx);
        })
      ) : (
        // Fallback: hardcoded steps (backward compat for orgs without promptConfig)
        <>
          {/* Step 1: Request Type */}
          {step >= 1 && (
            <div className="sc-step">
              <div className="sc-question">
                <div className="sc-avatar">🐴</div>
                <div className="sc-bubble">
                  <p>What type of request is this?</p>
                  <div className="sc-chips">
                    <button type="button" className={`sc-chip${requestType === 'op' ? ' selected' : ''}`} onClick={() => { setRequestType('op'); setStep(2); }} disabled={disabled || step > 1}>📋 OP Request ({nextYear})</button>
                    <button type="button" className={`sc-chip${requestType === 'urgent' ? ' selected' : ''}`} onClick={() => { setRequestType('urgent'); setStep(2); }} disabled={disabled || step > 1}>🚨 In-Year ({currentYear})</button>
                  </div>
                </div>
              </div>
              {requestType && step >= 2 && (
                <div className="sc-answer"><div className="sc-answer-chip">{requestType === 'op' ? `📋 OP Request (${nextYear})` : `🚨 In-Year (${currentYear})`}</div></div>
              )}
            </div>
          )}

          {/* Step 2: Entitlement */}
          {step >= 2 && (
            <div className="sc-step">
              <div className="sc-question">
                <div className="sc-avatar">🐴</div>
                <div className="sc-bubble">
                  <p>What's the estimated annualized entitlement?</p>
                  {step === 2 && (
                    <div className="sc-chips">
                      {ENTITLEMENT_PRESETS.map(p => (
                        <button key={p.label} type="button" className="sc-chip sc-chip-small" onClick={() => { setPromptValues(prev => ({ ...prev, annualized_entitlement: p.value })); setStep(3); }} disabled={disabled}>{p.label}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {step >= 3 && promptValues.annualized_entitlement !== undefined && (
                <div className="sc-answer"><div className="sc-answer-chip">💰 {promptValues.annualized_entitlement > 0 ? `$${promptValues.annualized_entitlement.toLocaleString('en-US')}/year` : 'TBD'}</div></div>
              )}
            </div>
          )}

          {/* Step 3: Impact Metric */}
          {step >= 3 && (
            <div className="sc-step">
              <div className="sc-question">
                <div className="sc-avatar">🐴</div>
                <div className="sc-bubble">
                  <p>How will we measure success?</p>
                  <div className="sc-chips">
                    {['DEA', 'Speed', 'Productivity Savings', 'Transportation Savings', 'Quality', 'Safety', 'VCPU'].map(metric => (
                      <button key={metric} type="button" className={`sc-chip${promptValues.impact_metrics === metric ? ' selected' : ''}`} onClick={() => {
                        setPromptValues(prev => ({ ...prev, impact_metrics: metric }));
                        setTimeout(() => {
                          onContinue({
                            requestType: requestType!,
                            targetYear: requestType === 'op' ? String(nextYear) : String(currentYear),
                            selectedOrgId: selectedOrg,
                            annualizedEntitlement: promptValues.annualized_entitlement || 0,
                            impactMetric: metric,
                          });
                        }, 300);
                      }} disabled={disabled || step > 3}>{metric}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div ref={flowEndRef} style={{ height: 1 }} />
    </div>
  );
};

export default StructuredCaptureCard;